import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TFunction } from 'i18next';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { DefaultTheme, useTheme } from 'styled-components';

import { GeodataStatus } from '../../../../../../typings/api/skymap/rest/v0/.common';
import { Icon } from '../../icon/icon';
import { Stack } from '../../stack/stack';
import { GeodataContext } from '../geodata-state';
import { geodataStatuses } from '../use-geodata';
import { AlignerContext } from './aligner-state';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getGeodataStatusIconText(status: GeodataStatus, t: TFunction<any>): string {
  switch (status) {
    case 'align_cameras':
      return t('statuses.alignCameras', { ns: 'cloudProcessing' });
    case 'align_photos_failed':
      return t('statuses.alignPhotosFailed', { ns: 'cloudProcessing' });
    case 'align_photos_pending':
      return t('statuses.alignPhotosPending', { ns: 'cloudProcessing' });
    case 'align_photos_preparing':
      return t('statuses.alignPhotosPreparing', { ns: 'cloudProcessing' });
    case 'build_depth_maps':
      return t('statuses.buildDepthMaps', { ns: 'cloudProcessing' });
    case 'build_model':
      return t('statuses.buildModel', { ns: 'cloudProcessing' });
    case 'build_orthomosaic':
      return t('statuses.buildOrthomosaic', { ns: 'cloudProcessing' });
    case 'build_tiled_model':
      return t('statuses.buildTiledModel', { ns: 'cloudProcessing' });
    case 'created':
      return t('statuses.created', { ns: 'cloudProcessing' });
    case 'export_points':
      return t('statuses.exportPoints', { ns: 'cloudProcessing' });
    case 'export_raster':
      return t('statuses.exportRaster', { ns: 'cloudProcessing' });
    case 'generate_pointcloud_failed':
      return t('statuses.generatePointcloudFailed', { ns: 'cloudProcessing' });
    case 'generate_pointcloud_finished':
      return t('statuses.generatePointcloudFinished', { ns: 'cloudProcessing' });
    case 'generate_skyview_data_failed':
      return t('statuses.generateSkyviewDataFailed', { ns: 'cloudProcessing' });
    case 'generate_skyview_data_finished':
      return t('statuses.generateSkyviewDataFinished', { ns: 'cloudProcessing' });
    case 'generate_skyview_data_pending':
      return t('statuses.generateSkyviewDataPending', { ns: 'cloudProcessing' });
    case 'generate_tif_finished':
      return t('statuses.generateTifFinished', { ns: 'cloudProcessing' });
    case 'generate_tif_pending':
      return t('statuses.generateTifPending', { ns: 'cloudProcessing' });
    case 'generate_tif_preparing':
      return t('statuses.generateTifPreparing', { ns: 'cloudProcessing' });
    case 'match_photos':
      return t('statuses.matchPhotos', { ns: 'cloudProcessing' });
    case 'optimize_cameras':
      return t('statuses.optimizeCameras', { ns: 'cloudProcessing' });
    case 'optimize_pending':
      return t('statuses.optimizePending', { ns: 'cloudProcessing' });
    case 'optimize_running':
      return t('statuses.optimizeRunning', { ns: 'cloudProcessing' });
    case 'user_move_flags':
      return t('statuses.userMoveFlags', { ns: 'cloudProcessing' });
    case 'generating_skyview_data' as GeodataStatus:
      return t('statuses.generatingSkyviewData', { ns: 'cloudProcessing' });
    case 'optimize_pending_reset_alignment' as GeodataStatus:
      return t('statuses.optimizePendingResetAlignment', { ns: 'cloudProcessing' });
    default:
      return '...';
  }
}

export function getGeodataStatusIconContent(
  status: GeodataStatus,
  theme: DefaultTheme,
  t: TFunction,
  readyToQuickOptimize: boolean,
): {
  icon: IconProp;
  text: string;
  spin: boolean;
  color: string;
} {
  if (readyToQuickOptimize && status === 'user_move_flags') {
    return {
      icon: ['fad', 'network-wired'],
      color: theme.color.gray.medium,
      spin: false,
      text: t('statuses.readyToQuickOptimize', { ns: 'cloudProcessing' }),
    };
  }

  const text = getGeodataStatusIconText(status, t);

  if (geodataStatuses.failedStatuses.includes(status)) {
    return {
      icon: ['fad', 'warning'],
      color: theme.color.red,
      spin: false,
      text,
    };
  }

  if (geodataStatuses.successStatuses.includes(status)) {
    return {
      icon: ['fad', 'check-circle'],
      color: theme.color.green,
      spin: false,
      text,
    };
  }

  return {
    icon: ['fad', 'spinner'],
    color: theme.color.blue,
    spin: true,
    text,
  };
}

export const GeodataStatusIcon = ({ showWms }: { showWms: boolean }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { geodata } = useContext(GeodataContext);
  const { readyToQuickOptimize } = useContext(AlignerContext);

  const iconConfig = useMemo(
    () => getGeodataStatusIconContent(geodata.status, theme, t, readyToQuickOptimize),
    [geodata, theme, t, readyToQuickOptimize],
  );

  if (geodataStatuses.placeMarkerStatuses.includes(geodata.status) && !readyToQuickOptimize) {
    return null;
  }

  return (
    <StatusIconStyle>
      <Stack direction="row" spacing={0.5}>
        <Icon
          color={iconConfig.color}
          fixedWidth={true}
          icon={iconConfig.icon}
          size="lg"
          spin={iconConfig.spin}
          title={iconConfig.text}
        />
        <StatusIconLabel showWms={showWms}>{iconConfig.text}</StatusIconLabel>
      </Stack>
    </StatusIconStyle>
  );
};

const StatusIconStyle = styled.div`
  position: absolute;
  left: 1em;
  bottom: 1em;
`;

const StatusIconLabel = styled.label<{ showWms: boolean }>`
  color: ${({ showWms }) => (showWms ? 'black' : 'white')};
  font-weight: 500;
`;
